/**
 * CV API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { CV, CVListResponse, CVDuplicate, CVDuplicateListResponse, CVMergeResponse } from '../types';

interface ListCVsOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  poolIds?: string[];
  positionIds?: string[];
  jobIds?: string[];
  processingStatus?: string;
  search?: string;
  excludeJobId?: string;
  cvTagSlugs?: string[];
}

interface CVResponse {
  success: boolean;
  data: CV;
  message: string;
}

interface CVListApiResponse {
  success: boolean;
  data: CVListResponse;
  message: string;
}

interface TriggerParseResponse {
  success: boolean;
  data: { message_id: string };
  message: string;
}

export interface AttachmentSignedUrlData {
  signed_url: string;
  expires_at: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
  preview_available?: boolean;
}

interface AttachmentSignedUrlResponse {
  success: boolean;
  data: AttachmentSignedUrlData;
  message: string;
}

interface SkippedJob {
  job_id: string;
  reason: string;
}

interface AddJobsToCVResponse {
  added_count: number;
  skipped_count: number;
  job_ids: string[];
  skipped: SkippedJob[] | null;
  round_ids: string[];
}

interface AddJobsToCVApiResponse {
  success: boolean;
  data: AddJobsToCVResponse;
  message: string;
}

interface BulkAddCVsToPoolsResponse {
  added_count: number;
  skipped_count: number;
  cv_count: number;
  pool_count: number;
}

interface BulkAddCVsToPoolsApiResponse {
  success: boolean;
  data: BulkAddCVsToPoolsResponse;
  message: string;
}

class CVService {
  /**
   * List CVs with pagination and optional filters.
   * Converts page/size to skip/limit for backend API.
   */
  async listCVs(
    workspaceId: string,
    idToken: string,
    options: ListCVsOptions = {}
  ): Promise<CVListApiResponse> {
    const {
      page = 1,
      size = 10,
      sortBy = 'created_at',
      order = 'desc',
      poolIds,
      positionIds,
      jobIds,
      processingStatus,
      search,
      excludeJobId,
      cvTagSlugs,
    } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CVListApiResponse>(`/cvs`, {
      params: {
        skip,
        limit: size,
        sort_by: sortBy,
        order,
        ...(poolIds && poolIds.length > 0 && { pool_id: poolIds }),
        ...(positionIds && positionIds.length > 0 && { position_id: positionIds }),
        ...(jobIds && jobIds.length > 0 && { job_id: jobIds }),
        processing_status: processingStatus,
        search: search || undefined,
        ...(excludeJobId && { exclude_job_id: excludeJobId }),
        ...(cvTagSlugs && cvTagSlugs.length > 0 && { cv_tag_slug: cvTagSlugs }),
      },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list CVs');
    }

    return response.data;
  }

  /**
   * Get a single CV by ID.
   */
  async getCV(cvId: string, workspaceId: string, idToken: string): Promise<CV> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CVResponse>(`/cvs/${cvId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get CV');
    }

    return response.data.data;
  }

  /**
   * Trigger CV parser for a specific attachment.
   */
  async triggerParseAttachment(
    cvId: string,
    attachmentId: string,
    workspaceId: string,
    idToken: string
  ): Promise<{ message_id: string }> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<TriggerParseResponse>(
      `/cvs/${cvId}/attachments/${attachmentId}/parse`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to trigger parse');
    }

    return response.data.data;
  }

  /**
   * Get signed URL for viewing/downloading a CV attachment.
   */
  async getAttachmentSignedUrl(
    cvId: string,
    attachmentId: string,
    workspaceId: string,
    idToken: string,
    preview?: boolean
  ): Promise<AttachmentSignedUrlData> {
    const client = createApiClient(idToken, 'ats');
    const params: Record<string, string> = {};
    if (preview) params.preview = 'true';

    const response = await client.get<AttachmentSignedUrlResponse>(
      `/cvs/${cvId}/attachments/${attachmentId}/signed-url`,
      { params, headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get signed URL');
    }

    return response.data.data;
  }

  /**
   * Add a CV to multiple jobs at each job's first pipeline round.
   */
  async addJobsToCV(
    cvId: string,
    workspaceId: string,
    idToken: string,
    jobIds: string[]
  ): Promise<AddJobsToCVResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<AddJobsToCVApiResponse>(
      `/cvs/${cvId}/jobs/add`,
      { job_ids: jobIds },
      {
        headers: { 'X-Workspace-ID': workspaceId },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to add jobs to CV');
    }

    return response.data.data;
  }

  /**
   * Bulk-add CVs to pools. Handles all cases:
   *   - 1 CV  → N pools : pass [cvId],  poolIds
   *   - N CVs → 1 pool  : pass cvIds,   [poolId]
   *   - N CVs → M pools : pass cvIds,   poolIds
   */
  async bulkAddCVsToPools(
    cvIds: string[],
    workspaceId: string,
    idToken: string,
    poolIds: string[]
  ): Promise<BulkAddCVsToPoolsResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkAddCVsToPoolsApiResponse>(
      `/cvs/bulk/pools/add`,
      { cv_ids: cvIds, pool_ids: poolIds },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to add CVs to pools');
    }

    return response.data.data;
  }

  async bulkSyncCVPools(
    cvIds: string[],
    workspaceId: string,
    idToken: string,
    poolIdsToAdd: string[],
    poolIdsToRemove: string[]
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{ success: boolean; message: string }>(
      `/cvs/bulk/pools/sync`,
      { cv_ids: cvIds, pool_ids_to_add: poolIdsToAdd, pool_ids_to_remove: poolIdsToRemove },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to sync CV pools');
    }
  }

  async bulkRemoveCVsFromPools(
    cvIds: string[],
    workspaceId: string,
    idToken: string,
    poolIds: string[]
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{ success: boolean; message: string }>(
      `/cvs/bulk/pools/remove`,
      { cv_ids: cvIds, pool_ids: poolIds },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to remove CVs from pools');
    }
  }

  /**
   * Delete a CV and all related data (attachments, jobs, notes, GCS files).
   */
  async deleteCV(cvId: string, workspaceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.delete<{ success: boolean; message: string }>(
      `/cvs/${cvId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete CV');
    }
  }

  /**
   * Get pending duplicates for a CV.
   */
  async getDuplicates(
    cvId: string,
    workspaceId: string,
    idToken: string
  ): Promise<CVDuplicate[]> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<{
      success: boolean;
      data: CVDuplicateListResponse;
      message: string;
    }>(`/cvs/${cvId}/duplicates`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get duplicates');
    }

    return response.data.data.items;
  }

  /**
   * Merge duplicate CVs (keep older, update with newer data).
   */
  async mergeDuplicate(
    cvId: string,
    duplicateId: string,
    workspaceId: string,
    idToken: string
  ): Promise<CVMergeResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{
      success: boolean;
      data: CVMergeResponse;
      message: string;
    }>(
      `/cvs/${cvId}/duplicates/${duplicateId}/merge`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to merge duplicates');
    }

    return response.data.data;
  }

  /**
   * Dismiss a duplicate (choose not to merge).
   */
  async dismissDuplicate(
    cvId: string,
    duplicateId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{
      success: boolean;
      message: string;
    }>(
      `/cvs/${cvId}/duplicates/${duplicateId}/dismiss`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to dismiss duplicate');
    }
  }

  /**
   * Manually trigger duplicate check for a CV.
   */
  async checkDuplicates(
    cvId: string,
    workspaceId: string,
    idToken: string
  ): Promise<CVDuplicate[]> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{
      success: boolean;
      data: CVDuplicateListResponse;
      message: string;
    }>(
      `/cvs/${cvId}/check-duplicates`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to check duplicates');
    }

    return response.data.data.items;
  }
}

const cvService = new CVService();
export default cvService;
